<script setup>
import { computed, ref } from 'vue';
import Multiselect from 'vue-multiselect';
import { useField } from 'vee-validate';
import { sortArray } from 'utils';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: true
  },
  validatorName: {
    type: String,
    required: false,
    default: null,
  },
  rules: {
    type: String,
    required: false,
    default: null
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  showRequiredIndicator: {
    type: Boolean,
    required: false,
    default: true
  },
  showOtherOption: {
    type: Boolean,
    required: false,
    default: false
  },
  forceRequiredForOtherOption: {
    type: Boolean,
    required: false,
    default: false
  }
});

const {
  errorMessage,
  setValue,
  value,
} = useField(props.name, props.rules, {
  label: props.validatorName ?? props.label,
  validateOnValueUpdate: false,
});

const otherOptionActive = computed(() => props.showOtherOption
  && value.value
  && Array.isArray(value.value)
  && value.value.find((o) => o.value === 'other'));

const other = ref(otherOptionActive.value ? value.value.customInput : null);

const isRequired = computed(() => props.rules && props.rules.indexOf('required') !== -1);

const otherRules = computed(() => {
  if (props.forceRequiredForOtherOption && !isRequired.value) {
    return props.rules ? `${props.rules} required` : 'required';
  }

  return props.rules;
});
const isOtherRequired = computed(() => otherRules.value && otherRules.value.indexOf('required') !== -1);

const preparedOptions = computed(() => {
  const options = Object.keys(props.options).map((v) => ({ title: props.options[v], value: v }));

  if (props.showOtherOption) {
    options.push({ title: t('generic.otherOption'), value: 'other' });
  }

  return sortArray(options, 'title', 'asc');
});

function handleInput(selectedEntries) {
  setValue(selectedEntries, true);
}

function handleCustomInput(otherValue) {
  other.value = otherValue;
  const result = value.value;
  const otherOption = result.find((o) => o.value === 'other');
  otherOption.customInput = otherValue;

  setValue(result, true);
}
</script>

<template>
  <div
    class="ce-field ce-multiSelectField"
    :class="{'ce-multiSelectField--withOtherOption': otherOptionActive}">
    <div>
      <label
        v-if="label"
        :for="name">
        {{ label }} <span v-if="isRequired && showRequiredIndicator">*</span>
        <span
          v-if="errorMessage"
          class="ms-2 ml1 regular ce-colorError">
          {{ errorMessage }}
        </span>
      </label>

      <div class="ce-multiSelectField-fieldWrapper">
        <div>
          <multiselect
            :id="name"
            :name="name"
            :model-value="value"
            :options="preparedOptions"
            label="title"
            track-by="value"
            :multiple="true"
            :preserve-search="false"
            :hide-selected="true"
            placeholder=""
            :disabled="disabled"
            @update:modelValue="handleInput" />
        </div>
      </div>
    </div>

    <div v-if="otherOptionActive">
      <label v-if="label">
        {{ $t('generic.otherOption') }} <span v-if="isOtherRequired && showRequiredIndicator">*</span>
        <span
          v-if="errorMessage"
          class="ms-2 ml1 regular ce-colorError">
          {{ errorMessage }}
        </span>
      </label>
      <input
        type="text"
        :disabled="disabled"
        :value="other"
        @input="handleCustomInput($event.target.value)"
        autocomplete=""
        autocapitalize="">
    </div>
  </div>
</template>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-field.ce-multiSelectField {
  &.ce-multiSelectField--withOtherOption {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;

    > div {
      width: 100%;

      @media screen and (min-width: @breakpoint-xs) {
        &:first-child {
          width: 70%;
          padding-right: @space-2;
        }

        &:last-child {
          width: 30%;
          padding-left: @space-2;
        }
      }

      .ce-field {
        margin-bottom: 0;
      }
    }
  }
}
</style>
